import * as amplitude from '@amplitude/analytics-browser';
import { Capacitor } from '@capacitor/core';
import { jsVersionWithBuildNumber } from './app';
import { SubscriptionSource, SubscriptionStatus } from '@sworkit/api';

import SworkitMobile from './sworkit-mobile/sw-mobile';
import { User } from 'firebase/auth';
import { removeUndefinedAndNaNRecursive } from './utils';

export enum AmplitudeEvents {
  // should be verb noun - past tense
  added_to_favorites = 'added_to_favorites',
  attemped_to_share = 'attemped_to_share',
  attemped_to_share_completed_workout = 'attemped_to_share_completed_workout',
  attempted_to_review = 'attempted_to_review',
  changed_workout_settings = 'changed_workout_settings',
  contacted_support = 'contacted_support',
  connect_devices = 'connect_devices',
  create_account = 'create_account',
  created_custom_workout = 'created_custom_workout',
  created_shareable_image = 'created_shareable_image',
  enabled_integration_health = 'enabled_integration_health',
  enabled_integration_health_read = 'enabled_integration_health_read',
  enabled_integration_mfp = 'enabled_integration_mfp',
  enabled_integration_strava = 'enabled_integration_strava',
  enabled_external_provider_access = 'enabled_external_provider_access',
  exit_beta = 'exit_beta',
  filtered_exercise_library = 'filtered_exercise_library',
  filtered_workout_library = 'filtered_workout_library',
  first_workout_completed = 'first_workout_completed',
  get_new_recommendations = 'get_new_recommendations',
  joined_challenge = 'joined_challenge',
  left_challenge = 'left_challenge',
  logged_external_activity = 'logged_external_activity',
  no_results_exercise_library = 'no_results_exercise_library',
  onboarding_completed = 'onboarding_completed',
  opened_ask_a_trainer = 'opened_ask_a_trainer',
  opened_help_center = 'opened_help_center',
  opened_sworkit = 'opened_sworkit',
  opened_sworkit_first_time = 'opened_sworkit_first_time',
  page_view = 'page_view',
  played_sworkit_music = 'played_sworkit_music',
  preloading_error = 'preloading_error',
  saved_workout_log_via_cf = 'saved_workout_log_via_cf',
  scheduled_daily_reminder = 'scheduled_daily_reminder',
  scheduled_inactivity_reminder = 'scheduled_inactivity_reminder',
  scheduled_next_workout = 'scheduled_next_workout',
  searched_exercise_library = 'searched_exercise_library',
  searched_workout_library = 'searched_workout_library',
  second_workout_completed = 'second_workout_completed',
  sign_in = 'sign_in',
  started_live_player = 'started_live_player',
  started_stripe_subscription = 'started_stripe_subscription',
  subscribe_button_tapped = 'subscribe_button_tapped',
  tapped_cancel_stripe = 'tapped_cancel_stripe',
  tapped_customize_workout = 'tapped_customize_workout',
  tapped_exercise_library_exercise = 'tapped_exercise_library_exercise',
  tapped_my_sworkit_tab = 'tapped_my_sworkit_tab',
  tapped_workout_library_workout = 'tapped_workout_library_workout',
  third_workout_completed = 'third_workout_completed',
  view_purchase_screen = 'view_purchase_screen',
  viewed_assessment_page = 'viewed_assessment_page',
  viewed_assessment_stats_page = 'viewed_assessment_stats_page',
  viewed_assessments_page = 'viewed_assessments_page',
  viewed_choose_challenge_page = 'viewed_choose_challenge_page',
  viewed_collection_page = 'viewed_collection_page',
  viewed_create_profile_page = 'viewed_create_profile_page',
  viewed_end_workout_page = 'viewed_end_workout_page',
  viewed_exercise_library = 'viewed_exercise_library',
  viewed_exercise_preview = 'viewed_exercise_preview',
  viewed_home_page = 'viewed_home_page',
  viewed_milestone = 'viewed_milestone',
  viewed_music_select_page = 'viewed_music_select_page',
  viewed_my_stats_page = 'viewed_my_stats_page',
  viewed_my_sworkit_page = 'viewed_my_sworkit_page',
  viewed_promo_screen = 'viewed_promo_screen',
  viewed_rec_activity_page = 'viewed_rec_activity_page',
  viewed_rec_age_page = 'viewed_rec_age_page',
  viewed_rec_collection_page = 'viewed_rec_collection_page',
  viewed_rec_gender_page = 'viewed_rec_gender_page',
  viewed_rec_goal_page = 'viewed_rec_goal_page',
  viewed_rec_injuries_page = 'viewed_rec_injuries_page',
  viewed_rec_interest_page = 'viewed_rec_interest_page',
  viewed_rec_pain_page = 'viewed_rec_pain_page',
  viewed_rec_plan_page = 'viewed_rec_plan_page',
  viewed_rec_primary_page = 'viewed_rec_primary_page',
  viewed_rec_push_page = 'viewed_rec_push_page',
  viewed_rec_secondary_page = 'viewed_rec_secondary_page',
  viewed_rec_weight_page = 'viewed_rec_weight_page',
  viewed_settings_page = 'viewed_settings_page',
  viewed_slow_loading_workout = 'viewed_slow_loading_workout',
  viewed_stripe_subscribe_error = 'viewed_stripe_subscribe_error',
  viewed_studio_all_page = 'viewed_studio_all_page',
  viewed_studio_collection_page = 'viewed_studio_collection_page',
  viewed_studio_page = 'viewed_studio_page',
  viewed_time_select_page = 'viewed_time_select_page',
  viewed_welcome_page = 'viewed_welcome_page',
  viewed_workout_library = 'viewed_workout_library',
  viewed_workout_log_import_page = 'viewed_workout_log_import_page',
  viewed_workout_log_share_page = 'viewed_workout_log_share_page',
  viewed_workout_player_page = 'viewed_workout_player_page',
  viewed_workout_preview_page = 'viewed_workout_preview_page',
  viewed_studio_player_page = 'viewed_studio_player_page',
  viewed_year_in_review = 'viewed_year_in_review',
  workout_ended = 'workout_ended',
  workout_started = 'workout_started',
  meditation_ended = 'meditation_ended',
  meditation_started = 'meditation_started',
  assessment_started = 'assessment_started',
  assessment_completed = 'assessment_completed',
  curriculum_only_popup = 'curriculum_only_popup',
  curriculum_only_learn_more = 'curriculum_only_learn_more',
  content_ended = 'content_ended',
}

export interface AmplitudeUserProperties {
  // These attributes are NOT be tied to data in firestore
  // If it's in firestore - it should be added via sw-cloud-functions
  adid: string;
  android_id: string;
  email_verified: boolean;
  experiment_1_ad: string;
  experiment_2_welcome: 'three_welcome_slides_a' | 'one_welcome_slide_b';
  experiment_3_welcome_exercise:
    | 'side-stretch'
    | 'plank-jack'
    | 'mountain-climber'
    | 'resistance-loop-squats'
    | 'arm-raise'
    | 'hand-to-hand-kettlebell-swing';
  experiment_4_paywall: boolean; // paywall after register
  experiment_5_onboarding: 'recommendation_for_all' | 'four_questions'; // four_questions is baseline
  experiment_6_free_workout: 'show_free_workout' | 'no_free_workout';
  idfa: string;
  idfv: string;
  integration_health: boolean;
  integration_health_read: boolean;
  is_active_subscriber: boolean;
  platform: 'ios' | 'android' | 'web';
  push_notification_granted: boolean;
  session_count: number;
  sign_up_date: string;
  subscription_source: SubscriptionSource;
  subscription_status: SubscriptionStatus;
}

export function amplitudeInit() {
  //initialize and configure the new Browser SDK 2
  // const amplitude_key = '1d68c601f6298be21ab174e8667728d9'; // dev
  const amplitude_key = '1044240cadee20fec68c223c92e3808b'; // prod
  const options = {
    sessionTimeout: 70 * 60 * 1000, // 70 mins
    appVersion: jsVersionWithBuildNumber,
    autocapture: {
      attribution: false,
      pageViews: true,
      sessions: true,
      formInteractions: true,
      fileDownloads: false,
      elementInteractions: true,
    },
  };
  amplitude.init(amplitude_key, options);
}

export function amplitudeIdentifyUser(
  user: User,
  signUpDate: string,
  currentState: SworkitState,
) {
  //new amplitude sdk plugin
  amplitude.setUserId(user.uid);

  const update: Partial<AmplitudeUserProperties> =
    removeUndefinedAndNaNRecursive({
      sign_up_date: signUpDate ? new Date(signUpDate).toISOString() : undefined,
      platform: Capacitor.getPlatform() as 'ios' | 'android' | 'web',
      email_verified: user.emailVerified,
    });
  if (currentState?.health?.healthWriteEnabled) {
    update.integration_health = true;
  }
  if (currentState?.health?.healthReadEnabled) {
    update.integration_health_read = true;
  }
  if (
    currentState &&
    currentState.notifications &&
    currentState.notifications.notificationsPermission
  ) {
    update.push_notification_granted = true;
  }
  return amplitudeSetUserProperties(update);
}

export function amplitudeSetUserProperties(
  properties: Partial<AmplitudeUserProperties>,
) {
  return amplitude.identify(new amplitude.Identify().set("properties", properties));
}

// async function amplitudeSetVersion() {
//   const version = jsVersionWithBuildNumber;
//   return amplitude.setVersionName(version);
// }

export function amplitudeLogEvent(eventName: AmplitudeEvents, data?: any) {
  return amplitude.track(eventName, data);
}

export async function sendSworkitOpenedToAmplitude() {
  const adIds = await getAdIds();
  return amplitude.track(AmplitudeEvents.opened_sworkit, adIds);
}

async function getAdIds() {
  if (Capacitor.isNativePlatform()) {
    const { idfa, idfv, adid, android_id } =
      await SworkitMobile.getAdvertisingId();
    return {
      idfa,
      idfv,
      adid,
      android_id,
    };
  }
  return {};
}
